import { NotificationApplication, Topic } from '@/core/notifications/model';

// Step 1 of the subscription wizard: pick a topic and name the subscription.
// Grouping by application only shows up when it's actually useful — pipelines
// (and any other caller handing over an already-scoped topic subset) get the
// plain flat list for free, no special-case prop required.
export default function StepTopic({
  topics,
  applications,
  topicKey,
  name,
  isEdit,
  onSelectTopic,
  onNameChange,
}: {
  topics: Topic[];
  applications: NotificationApplication[];
  topicKey: string;
  name: string;
  isEdit: boolean;
  onSelectTopic: (topicKey: string) => void;
  onNameChange: (name: string) => void;
}) {
  const distinctApplicationIds = new Set(
    topics.map(topic => topic.applicationId).filter((id): id is string => !!id),
  );
  const showAppGrouping = applications.length > 1 && distinctApplicationIds.size > 1;
  const applicationsWithTopics = applications.filter(app =>
    topics.some(topic => topic.applicationId === app.id),
  );
  const ungroupedTopics = topics.filter(
    topic => !distinctApplicationIds.has(topic.applicationId ?? ''),
  );

  const selectedTopic = topics.find(topic => topic.key === topicKey);

  return (
    <div>
      <div className="mb-3">
        <label className="form-label" htmlFor="wizard-subscription-topic">
          Topic
        </label>
        <select
          id="wizard-subscription-topic"
          className="form-select"
          value={topicKey}
          disabled={isEdit}
          onChange={event => onSelectTopic(event.target.value)}
        >
          {showAppGrouping ? (
            <>
              {applicationsWithTopics.map(app => (
                <optgroup key={app.id} label={app.name}>
                  {topics
                    .filter(topic => topic.applicationId === app.id)
                    .map(topic => (
                      <option key={topic.key} value={topic.key}>
                        {topic.displayName ?? topic.key}
                      </option>
                    ))}
                </optgroup>
              ))}
              {ungroupedTopics.map(topic => (
                <option key={topic.key} value={topic.key}>
                  {topic.displayName ?? topic.key}
                </option>
              ))}
            </>
          ) : (
            topics.map(topic => (
              <option key={topic.key} value={topic.key}>
                {topic.displayName ?? topic.key}
              </option>
            ))
          )}
        </select>
        <div className="form-text">
          {isEdit ? 'Topic cannot be changed after creation.' : (selectedTopic?.description ?? '')}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="wizard-subscription-name">
          Name
        </label>
        <input
          id="wizard-subscription-name"
          className="form-control"
          value={name}
          onChange={event => onNameChange(event.target.value)}
        />
      </div>
    </div>
  );
}
